/**
 * Utility functions for handling ValidationStatus in surveys
 */

export enum ValidationStatus {
    Fail = 0,
    Pass = 1,
    NoLongerAvailable = 2,
    NeverSold = 3
}

export const validationStatusMap: Record<string, string> = {
    "0": "Fail",
    "1": "Pass", 
    "2": "NoLongerAvailable",
    "3": "NeverSold"
};

/**
 * Gets a user-friendly descriptive label for unavailable materials based on ValidationStatus
 * @param validationStatus The validation status value (as string or number)
 * @returns Descriptive label for the material status
 */
import { IStringLocalizer } from "./IStringLocalizer";

export function getUnavailableMaterialLabel(
    validationStatus: string | number | undefined | null,
    localizer: IStringLocalizer
): string {
    if (!validationStatus) {
        return "";
    }

    const statusString = validationStatus.toString();

    switch (statusString) {
        case "2": // NoLongerAvailable
            return localizer.localizeString("This product is no longer available.");
        case "3": // NeverSold
            return localizer.localizeString("This product was never sold by my company.");
        default:
            return "";
    }
}

/**
 * Formats the complete display text for unavailable materials
 * @param materialName The name of the material/product
 * @param description The description of the material (optional)
 * @param validationStatus The validation status value
 * @returns Complete formatted display text
 */
export function formatUnavailableMaterialDisplay(
    materialName: string,
    description: string | undefined | null,
    validationStatus: string | number | undefined | null,
    localizer: IStringLocalizer
): string {
    const statusLabel = getUnavailableMaterialLabel(validationStatus, localizer);

    if (!statusLabel) {
        return materialName;
    }
    
    // Format: "Material Name - Description - Status" or "Material Name - Status" if no description
    if (description && description.trim()) {
        return `${materialName} - ${description} - ${statusLabel}`;
    } else {
        return `${materialName} - ${statusLabel}`;
    }
}

/**
 * Checks if a validation status represents an unavailable material
 * @param validationStatus The validation status value
 * @returns True if the material is unavailable (NoLongerAvailable or NeverSold)
 */
export function isUnavailableMaterial(validationStatus: string | number | undefined | null): boolean {
    if (!validationStatus) {
        return false;
    }
    
    const statusString = validationStatus.toString();
    return statusString === "2" || statusString === "3";
}